import { type FC } from 'react';
import {
  UiButton, UiDrawer, UiDrawerBody, UiDrawerCloseButton,
  UiDrawerContent,
  UiDrawerOverlay,
  UiHStack,
  UiIconPencilSimple,
  UiIconPlusCircle,
  UiStack,
  UiText,
} from '@/lib/ui';
import { useQuery } from '@tanstack/react-query';
import { loadRegistrationInfoFormResponse, type InfoFormResponse, InfoFormType, type InfoForm, type InfoFormByAttendeeCategoryQuery, infoFormByAttendeeCategoryQueryKey, loadInfoFormByAttendeeCategory, infoFormResponseQueryKey, type Registration } from '@/api/registration';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import { useTenantApi } from '@/account/hook/useTenantApi';
import InformationList from './InformationList';
import { useDisclosure } from '@chakra-ui/react';
import FullScreen from '@/base/Modal/FullScreen';
import GenericInfoForm from './GenericInfoForm';
import BaseButtonIconText from '@/base/Button/IconText';
import BaseButtonGhost from '@/base/Button/Ghost';
import { type ApiResponseSingle } from '@/api/tenantClient';

interface PersonalInfoSessionProps {
  registration: Registration
}

const PersonalInfoSession: FC<PersonalInfoSessionProps> = ({ registration }) => {
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const infoFormByAttendeeCategoryQuery = useQuery<InfoForm, Error>(
    [infoFormByAttendeeCategoryQueryKey, { registrationId: registration?.id, formType: InfoFormType.PERSONAL }],
    async () => { return await fetchInfoFormByAttendeeCategory(registration?.id); },
    {
      enabled: !!registration && !isApiPreparing,
    }
  );

  async function fetchInfoFormByAttendeeCategory(registrationId: number | undefined): Promise<InfoForm> {
    if (typeof registrationId === 'undefined') {
      return await Promise.reject(new Error('Invalid registrationId'));
    }

    const params: InfoFormByAttendeeCategoryQuery = { formType: InfoFormType.PERSONAL, registrationId };
    const result = await loadInfoFormByAttendeeCategory(createTenantApiRequest)(params);
    return result.item ?? null;
  }

  const personalInfoQuery = useQuery<ApiResponseSingle<InfoFormResponse>, Error>(
    [infoFormResponseQueryKey, { registrationId: registration?.id, type: InfoFormType.PERSONAL }],
    async () => { return await fetchRegistrationInfoFormResponse(registration?.id); },
    {
      enabled: !!registration?.id && !isApiPreparing,
    }
  );

  async function fetchRegistrationInfoFormResponse(registrationId: number | undefined): Promise<ApiResponseSingle<InfoFormResponse>> {
    if (typeof registrationId === 'undefined') {
      return await Promise.reject(new Error('Invalid registrationId'));
    }

    return await loadRegistrationInfoFormResponse(createTenantApiRequest)({
      registrationId,
      type: InfoFormType.PERSONAL
    });
  }

  const toModify = () => {
    onOpen();
  };

  return (
    <UiStack>
      <QueryContainer query={infoFormByAttendeeCategoryQuery}>
        {(infoForm) => {
          return (
            <QueryContainer query={personalInfoQuery}>
              {(personalInfoFormResponse) => {
                if (!personalInfoFormResponse.item) {
                  return <></>;
                }
                return (
                  <UiStack spacing={4}>
                    <UiHStack spacing={4}>
                      <UiText fontWeight={'bold'}>
                        Personal information
                      </UiText>
                      {/* <UiButton */}
                      {/*  rightIcon={(<UiIconPencilSimple color={'primary.500'} size={'xl'} weight={'bold'} />)} */}
                      {/*  variant={'ghost'} */}
                      {/*  colorScheme={'primary'} */}
                      {/*  fontSize={'md'} */}
                      {/*  // fontWeight={'bold'} */}
                      {/*  onClick={toModify} */}
                      {/* > */}
                      {/*  Modify */}
                      {/* </UiButton> */}
                      {/* <BaseButtonPlainIconEdit onClick={toModify} /> */}
                      <BaseButtonIconText Icon={UiIconPencilSimple} onClick={toModify}>Edit</BaseButtonIconText>
                    </UiHStack>
                    <InformationList
                      fieldsMetadata={infoForm.config.fieldsMetadata}
                      response={personalInfoFormResponse.item.response}
                      shadow={'none'}
                      borderColor={'gray.300'}
                      borderWidth={'1px'}
                      borderStyle={'solid'}
                    />
                    <FullScreen isOpen={isOpen} onClose={onClose}>
                      <GenericInfoForm
                        infoForm={infoForm}
                        infoFormResponse={personalInfoFormResponse.item}
                        registration={registration}
                        onSuccess={onClose}
                      />
                    </FullScreen>
                  </UiStack>
                );
              }}
            </QueryContainer>
          );
        }}
      </QueryContainer>
    </UiStack>
  );
};

export default PersonalInfoSession;
